/* global google */

/**
 * Componente Google Maps riutilizzabile per visualizzare percorsi
 * Usato sia nella home page (booking-search) che nella pagina results
 */
export class GoogleMapsRoute {
  constructor(elementId, options = {}) {
    this.elementId = elementId
    this.options = {
      center: { lat: 40.7303, lng: 13.901 }, // Centro di Ischia
      zoom: 13,
      ...options,
    }

    this.map = null
    this.directionsService = null
    this.directionsRenderer = null
    this.markerFrom = null
    this.markerTo = null
    this.labelFrom = null
    this.labelTo = null
    this.routeInfo = null
    this.routeRequestId = 0 // Per gestire richieste asincrone concorrenti
    this._pendingRoute = null // Percorso richiesto prima che la mappa fosse pronta
    this._routeBounds = null // Bounds dell'ultimo percorso, per riadattare la vista

    this.init()
  }

  init() {
    const mapElement = document.getElementById(this.elementId)
    if (!mapElement) return

    if (typeof google === 'undefined' || !google.maps) {
      // Riprova dopo un po' se Google Maps non è ancora caricato
      setTimeout(() => this.init(), 200)
      return
    }

    // Crea la mappa
    this.map = new google.maps.Map(mapElement, {
      center: this.options.center,
      zoom: this.options.zoom,
      disableDefaultUI: true,
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_TOP,
      },
      clickableIcons: false,
      styles: this.getMapStyles(),
    })

    // Inizializza Directions Service e Renderer
    this.directionsService = new google.maps.DirectionsService()
    this.directionsRenderer = new google.maps.DirectionsRenderer({
      map: this.map,
      suppressMarkers: true,
      polylineOptions: {
        strokeColor: '#0d7ff2',
        strokeWeight: 5,
        strokeOpacity: 0.8,
      },
    })

    // Callback quando la mappa è pronta
    if (this.options.onReady) {
      this.options.onReady(this)
    }

    // Se ci sono dati iniziali, mostra il percorso
    if (this.options.from && this.options.to) {
      this.setRoute(this.options.from, this.options.to)
    }

    // Applica un eventuale percorso richiesto prima che la mappa fosse pronta
    if (this._pendingRoute) {
      const { from, to } = this._pendingRoute
      this._pendingRoute = null
      this.setRoute(from, to)
    }
  }

  getMapStyles() {
    return [
      {
        featureType: 'all',
        elementType: 'geometry',
        stylers: [{ saturation: -20 }],
      },
      {
        featureType: 'poi',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }],
      },
      {
        featureType: 'poi.park',
        elementType: 'geometry',
        stylers: [{ color: '#c8e6c9' }, { lightness: 20 }],
      },
      {
        featureType: 'transit',
        stylers: [{ visibility: 'off' }],
      },
      {
        featureType: 'road',
        elementType: 'geometry',
        stylers: [{ lightness: 30 }, { saturation: -50 }],
      },
      {
        featureType: 'road',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#666666' }],
      },
      {
        featureType: 'road.highway',
        elementType: 'geometry',
        stylers: [{ color: '#ffffff' }],
      },
      {
        featureType: 'road.highway',
        elementType: 'geometry.stroke',
        stylers: [{ color: '#dddddd' }],
      },
      {
        featureType: 'water',
        elementType: 'geometry',
        stylers: [{ color: '#a8d4f0' }],
      },
      {
        featureType: 'landscape',
        elementType: 'geometry',
        stylers: [{ color: '#f5f5f5' }],
      },
      {
        featureType: 'administrative',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#999999' }],
      },
    ]
  }

  hasValidCoords(location) {
    if (!location) return false
    const lat = parseFloat(location.lat)
    const lng = parseFloat(location.lng)
    return !isNaN(lat) && !isNaN(lng)
  }

  clearMarkers() {
    if (this.markerFrom) {
      this.markerFrom.setMap(null)
      this.markerFrom = null
    }
    if (this.markerTo) {
      this.markerTo.setMap(null)
      this.markerTo = null
    }
    if (this.labelFrom) {
      this.labelFrom.close()
      this.labelFrom = null
    }
    if (this.labelTo) {
      this.labelTo.close()
      this.labelTo = null
    }
  }

  /**
   * Imposta e calcola il percorso tra due località
   * @param {Object} from - { lat, lng, name }
   * @param {Object} to - { lat, lng, name }
   */
  async setRoute(from, to) {
    if (!this.map) {
      // Mappa non ancora pronta (script in caricamento): memorizza e applica al ready
      this._pendingRoute = { from, to }
      return
    }

    // Incrementa request ID per invalidare richieste precedenti
    this.routeRequestId++
    const currentRequestId = this.routeRequestId

    // Rimuovi marker e label esistenti
    this.clearMarkers()

    // Pulisci anche il percorso precedente
    this.directionsRenderer.setDirections({ routes: [] })
    this._routeBounds = null

    const fromHasCoords = this.hasValidCoords(from)
    const toHasCoords = this.hasValidCoords(to)

    // Se abbiamo entrambe le località, calcola il percorso
    if (from && to) {
      if (fromHasCoords && toHasCoords) {
        await this.calculateRoute(from, to, currentRequestId)
      } else {
        // Fallback a geocoding per nome
        await this.calculateRouteByName(from, to, currentRequestId)
      }
    } else {
      // Mostra solo i marker delle località selezionate
      this.routeInfo = null

      if (fromHasCoords) {
        this.addMarker('from', from)
        this.map.panTo({ lat: parseFloat(from.lat), lng: parseFloat(from.lng) })
        this.map.setZoom(15)
      }

      if (toHasCoords) {
        this.addMarker('to', to)
        this.map.panTo({ lat: parseFloat(to.lat), lng: parseFloat(to.lng) })
        this.map.setZoom(15)
      }

      // Se non c'è nessuna località, torna al centro
      if (!from && !to) {
        this.map.panTo(this.options.center)
        this.map.setZoom(this.options.zoom)
      }
    }
  }

  async calculateRoute(from, to, requestId) {
    if (!this.directionsService) return

    const origin = { lat: parseFloat(from.lat), lng: parseFloat(from.lng) }
    const destination = { lat: parseFloat(to.lat), lng: parseFloat(to.lng) }

    try {
      const result = await this.directionsService.route({
        origin,
        destination,
        travelMode: google.maps.TravelMode.DRIVING,
      })

      this.handleRouteResult(result, from, to, requestId)
    } catch (error) {
      console.error('Errore calcolo percorso con coordinate:', error)
      await this.calculateRouteByName(from, to, requestId)
    }
  }

  async calculateRouteByName(from, to, requestId) {
    if (!this.directionsService || !from?.name || !to?.name) {
      return
    }

    // Aggiungi "Ischia" per migliorare la precisione del geocoding
    const origin = `${from.name}, Ischia, Italy`
    const destination = `${to.name}, Ischia, Italy`

    try {
      const result = await this.directionsService.route({
        origin,
        destination,
        travelMode: google.maps.TravelMode.DRIVING,
      })

      this.handleRouteResult(result, from, to, requestId)
    } catch (error) {
      console.error('Errore calcolo percorso per nome:', error)
      if (requestId === this.routeRequestId) {
        this.routeInfo = null
      }
    }
  }

  handleRouteResult(result, from, to, requestId) {
    // Ignora risultati di richieste obsolete
    if (requestId !== this.routeRequestId) {
      return
    }

    this.directionsRenderer.setDirections(result)
    this._routeBounds = result.routes[0].bounds || null

    // Estrai le coordinate effettive dal risultato per i marker
    const leg = result.routes[0].legs[0]
    const startLoc = {
      lat: leg.start_location.lat(),
      lng: leg.start_location.lng(),
      name: from.name,
    }
    const endLoc = {
      lat: leg.end_location.lat(),
      lng: leg.end_location.lng(),
      name: to.name,
    }

    // Aggiungi marker personalizzati agli estremi del percorso
    this.addMarker('from', startLoc)
    this.addMarker('to', endLoc)

    // Estrai info percorso
    this.routeInfo = {
      distance: leg.distance.text,
      duration: leg.duration.text,
      distanceKm: leg.distance.value / 1000, // meters to km
      durationText: leg.duration.text,
    }

    // Callback se definita
    if (this.options.onRouteCalculated) {
      this.options.onRouteCalculated(this.routeInfo)
    }
  }

  addMarker(type, location) {
    if (!this.hasValidCoords(location)) {
      console.warn('Cannot add marker: invalid coordinates for', type, location)
      return
    }

    const isOrigin = type === 'from'
    const markerKey = isOrigin ? 'markerFrom' : 'markerTo'
    const labelKey = isOrigin ? 'labelFrom' : 'labelTo'

    // Rimuovi marker e label esistenti per questo tipo (evita duplicati)
    if (this[markerKey]) {
      this[markerKey].setMap(null)
      this[markerKey] = null
    }
    if (this[labelKey]) {
      this[labelKey].close()
      this[labelKey] = null
    }

    const position = {
      lat: parseFloat(location.lat),
      lng: parseFloat(location.lng),
    }

    // Marker con stile Uber-like
    this[markerKey] = new google.maps.Marker({
      position,
      map: this.map,
      draggable: !!this.options.draggableMarkers,
      icon: {
        path: isOrigin
          ? google.maps.SymbolPath.CIRCLE
          : 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z',
        scale: isOrigin ? 8 : 1.5,
        fillColor: isOrigin ? '#000000' : '#0d7ff2',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: isOrigin ? 3 : 2,
        anchor: isOrigin ? null : new google.maps.Point(12, 22),
      },
      title: this.options.draggableMarkers
        ? 'Trascina per spostare il punto'
        : location.name || '',
      cursor: this.options.draggableMarkers ? 'grab' : undefined,
    })

    // Riposizionamento del marker da parte del cliente: notifica il chiamante,
    // che valida (area servita + reverse geocoding) e decide se confermare o annullare.
    if (this.options.draggableMarkers) {
      // Memorizza la posizione di partenza per poter annullare un drop non valido.
      this[markerKey].addListener('dragstart', () => {
        this[markerKey + 'PreDrag'] = this[markerKey].getPosition()
      })
      if (this.options.onMarkerMoved) {
        this[markerKey].addListener('dragend', (e) => {
          this.options.onMarkerMoved(type, e.latLng.lat(), e.latLng.lng())
        })
      }
    }

    // Aggiungi label stile Uber (InfoWindow sempre aperta)
    const labelText = this.truncateText(location.name || '', 25)
    const headerText = isOrigin ? 'Partenza' : 'Arrivo'

    this[labelKey] = new google.maps.InfoWindow({
      headerContent: headerText,
      content: `
        <div style="
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 13px;
          font-weight: 500;
          color: #1a1a1a;
          padding: 0 12px 8px 12px;
          margin: 0;
          max-width: 200px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        ">${labelText}</div>
      `,
      disableAutoPan: true,
    })

    this[labelKey].open(this.map, this[markerKey])

    // Applica stili dopo che l'InfoWindow è renderizzata
    google.maps.event.addListenerOnce(this[labelKey], 'domready', () => {
      document.querySelectorAll('.gm-style-iw-c').forEach((el) => {
        el.style.padding = '8px'
        el.style.paddingTop = '0'
      })
      document.querySelectorAll('.gm-style-iw-d').forEach((el) => {
        el.style.overflow = 'visible'
      })
      document.querySelectorAll('.gm-style-iw-tc').forEach((el) => {
        el.style.display = 'none'
      })
    })
  }

  truncateText(text, maxLength) {
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + '...'
  }

  /**
   * Riposiziona programmaticamente un marker (es. revert dopo un drop non valido).
   * @param {'from'|'to'} type
   * @param {number} lat
   * @param {number} lng
   */
  setMarkerPosition(type, lat, lng) {
    const markerKey = type === 'from' ? 'markerFrom' : 'markerTo'
    const marker = this[markerKey]
    if (!marker) return
    const position = { lat: parseFloat(lat), lng: parseFloat(lng) }
    if (Number.isNaN(position.lat) || Number.isNaN(position.lng)) return
    marker.setPosition(position)
  }

  /**
   * Riporta un marker alla posizione che aveva all'inizio del trascinamento
   * (catturata in 'dragstart'). Usato per annullare un drop non valido.
   * @param {'from'|'to'} type
   */
  revertMarker(type) {
    const markerKey = type === 'from' ? 'markerFrom' : 'markerTo'
    const marker = this[markerKey]
    const preDrag = this[markerKey + 'PreDrag']
    if (marker && preDrag) {
      marker.setPosition(preDrag)
    }
  }

  /**
   * Mostra un singolo marker (es. posizione utente) senza percorso
   */
  showUserPosition(lat, lng, label) {
    if (!this.map) return
    this.clearMarkers()
    this.directionsRenderer.setDirections({ routes: [] })
    this.routeInfo = null
    this._routeBounds = null

    const position = { lat, lng }
    this.markerFrom = new google.maps.Marker({
      position,
      map: this.map,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: '#4285F4',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 3,
      },
      title: label || '',
    })

    if (label) {
      this.labelFrom = new google.maps.InfoWindow({
        content: `<div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:13px;font-weight:500;color:#1a1a1a;padding:4px 8px">${label}</div>`,
        disableAutoPan: true,
      })
      this.labelFrom.open(this.map, this.markerFrom)
    }

    this.map.panTo(position)
    this.map.setZoom(15)
  }

  /**
   * Restituisce le info del percorso corrente
   */
  getRouteInfo() {
    return this.routeInfo
  }

  /**
   * Riadatta la vista al contenitore corrente (es. dopo l'apertura a schermo
   * intero, quando le dimensioni della mappa cambiano). Reinquadra il percorso
   * se presente, altrimenti lascia il centro attuale.
   */
  refit() {
    if (!this.map) return
    if (this._routeBounds) {
      this.map.fitBounds(this._routeBounds)
    }
  }

  /**
   * Resetta la mappa allo stato iniziale
   */
  reset() {
    this.clearMarkers()
    this.directionsRenderer.setDirections({ routes: [] })
    this.routeInfo = null
    this._routeBounds = null
    this.map.panTo(this.options.center)
    this.map.setZoom(this.options.zoom)
  }
}

/**
 * Auto-inizializza la mappa nella pagina results se presente
 * Gestisce sia la mappa desktop che mobile
 */
export function initResultsMap() {
  if (!window.resultsMapData) return

  const { from, to } = window.resultsMapData

  // Prova prima desktop, poi mobile
  let mapElement = document.getElementById('results-map')
  let elementId = 'results-map'

  // Se desktop è nascosto (mobile), usa mobile
  if (!mapElement || mapElement.offsetParent === null) {
    mapElement = document.getElementById('results-map-mobile')
    elementId = 'results-map-mobile'
  }

  if (!mapElement) return

  // Espone l'istanza globalmente per permettere aggiornamenti dal form di modifica
  window.resultsMapInstance = new GoogleMapsRoute(elementId, {
    from,
    to,
    onRouteCalculated: (info) => {
      // Callback per aggiornare Alpine component
      if (window.onResultsRouteCalculated) {
        window.onResultsRouteCalculated(info)
      }
    },
  })
}

// Auto-init per pagina results
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initResultsMap)
} else {
  initResultsMap()
}
